import { useEffect, useState } from "react";
import { DAILY_FINISH_LINE } from "@shared/daily";
import type { LeaderboardView } from "@shared/types";
import Home from "./pages/Home";
import SoloRace from "./pages/SoloRace";
import RoomEntry from "./pages/RoomEntry";
import RoomGame from "./pages/RoomGame";
import QuickRace from "./pages/QuickRace";
import Leaderboard from "./pages/Leaderboard";
import Profile from "./pages/Profile";
import Privacy from "./pages/Privacy";
import Admin from "./pages/Admin";
import { recordSession } from "./lib/api";
import type { RoomSession, SoloConfig } from "./lib/api";
import type { LocalProfile } from "./lib/profile";

type Screen =
  | { name: "home" }
  | { name: "solo"; config: SoloConfig }
  | { name: "daily" }
  | { name: "quick" }
  | { name: "leaderboard"; initial: LeaderboardView | null }
  | { name: "profile"; profile: LocalProfile }
  | { name: "privacy" }
  | { name: "roomEntry" }
  | { name: "admin" }
  // `mode` distinguishes a bot Quick Race from a private friends room. Both use
  // RoomGame; only the usage counters care which one it was.
  | { name: "room"; session: RoomSession; mode: "quick" | "room" };

/** Path-based routing for the standalone pages that live at their own URL (so
 *  they're shareable/linkable and stay out of the game flow). */
const pathIs = (path: string): boolean =>
  typeof window !== "undefined" &&
  window.location.pathname.replace(/\/+$/, "") === path;
const isAdminPath = (): boolean => pathIs("/admin");
const isPrivacyPath = (): boolean => pathIs("/privacy");

/** Resolve the initial screen from the URL (admin/privacy deep links, else home). */
function initialScreen(): Screen {
  if (isAdminPath()) return { name: "admin" };
  if (isPrivacyPath()) return { name: "privacy" };
  return { name: "home" };
}

/** The Daily Challenge runs as a solo race over a fixed, date-seeded set. */
const DAILY_CONFIG: SoloConfig = {
  categories: [],
  difficulty: "mixed",
  canon: "protestant-66",
  finishLine: DAILY_FINISH_LINE,
};

export default function App() {
  const [screen, setScreen] = useState<Screen>(initialScreen);

  // Count one sit-down session per tab visit (aggregate, de-identified — feeds
  // the admin engagement metric). Best-effort; deduped in src/lib/api.ts.
  useEffect(() => {
    recordSession();
  }, []);

  const showLeaderboard = (initial: LeaderboardView | null = null) =>
    setScreen({ name: "leaderboard", initial });
  const showProfile = (profile: LocalProfile) =>
    setScreen({ name: "profile", profile });
  // Leaving a standalone page (admin/privacy) restores the root URL so a refresh
  // lands on Home.
  const goHome = () => {
    if (isAdminPath() || isPrivacyPath()) window.history.pushState({}, "", "/");
    setScreen({ name: "home" });
  };

  return (
    <div className="min-h-full">
      {screen.name === "home" && (
        <Home
          onStart={(config) => setScreen({ name: "solo", config })}
          onQuickRace={() => setScreen({ name: "quick" })}
          onRaceFriends={() => setScreen({ name: "roomEntry" })}
          onDaily={() => setScreen({ name: "daily" })}
          onLeaderboard={() => showLeaderboard(null)}
          onViewProfile={showProfile}
        />
      )}

      {screen.name === "solo" && (
        <SoloRace
          // Remount on each fresh start so internal state resets cleanly.
          key={JSON.stringify(screen.config)}
          config={screen.config}
          onHome={() => setScreen({ name: "home" })}
          onViewLeaderboard={showLeaderboard}
          onViewProfile={showProfile}
        />
      )}

      {screen.name === "daily" && (
        <SoloRace
          key="daily"
          config={DAILY_CONFIG}
          daily
          onHome={() => setScreen({ name: "home" })}
          onViewLeaderboard={showLeaderboard}
          onViewProfile={showProfile}
        />
      )}

      {screen.name === "profile" && (
        <Profile
          profile={screen.profile}
          onHome={() => setScreen({ name: "home" })}
        />
      )}

      {screen.name === "quick" && (
        <QuickRace
          onReady={(session) => setScreen({ name: "room", session, mode: "quick" })}
          onHome={() => setScreen({ name: "home" })}
        />
      )}

      {screen.name === "leaderboard" && (
        <Leaderboard
          initial={screen.initial}
          onHome={() => setScreen({ name: "home" })}
        />
      )}

      {screen.name === "roomEntry" && (
        <RoomEntry
          onEnter={(session) => setScreen({ name: "room", session, mode: "room" })}
          onBack={() => setScreen({ name: "home" })}
        />
      )}

      {screen.name === "privacy" && <Privacy onHome={goHome} />}

      {screen.name === "admin" && <Admin onHome={goHome} />}

      {screen.name === "room" && (
        <RoomGame
          key={screen.session.raceId + ":" + screen.session.playerId}
          session={screen.session}
          mode={screen.mode}
          onHome={() => setScreen({ name: "home" })}
        />
      )}
    </div>
  );
}
